import React from 'react';
import {
  Box,
  TextField,
  Button,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import dayjs, { Dayjs } from 'dayjs';
import EventPatternEditor from './EventPatternEditor';
import type { SchedulePreset } from '../../../types/frontend';
import type { RuleFormState } from '../utils/ruleFormUtils';

interface RuleScheduleSectionProps {
  form: RuleFormState;
  setField: <K extends keyof RuleFormState>(field: K, value: RuleFormState[K]) => void;
  hasAttemptedSave: boolean;
  validationErrors: Record<string, string>;
  onPatternValidityChange?: (valid: boolean) => void;
}

const PRESETS: SchedulePreset[] = ['hourly', 'daily', 'weekly', 'monthly'];
const PRESETS_WITH_TIME: SchedulePreset[] = ['daily', 'weekly', 'monthly'];

const RuleScheduleSection: React.FC<RuleScheduleSectionProps> = ({
  form,
  setField,
  hasAttemptedSave,
  validationErrors,
  onPatternValidityChange,
}) => {
  const showTimePicker =
    form.triggerType === 'schedule' &&
    !form.useCron &&
    PRESETS_WITH_TIME.includes(form.schedulePreset);

  const timeValueLocal: Dayjs = dayjs
    .utc()
    .hour(form.scheduleHour)
    .minute(form.scheduleMinute)
    .second(0)
    .local();

  const handleTimeChange = (next: Dayjs | null) => {
    if (!next || !next.isValid()) return;
    const utcTime = next.utc();
    setField('scheduleHour', utcTime.hour());
    setField('scheduleMinute', utcTime.minute());
  };

  const utcLabel = `${String(form.scheduleHour).padStart(2, '0')}:${String(form.scheduleMinute).padStart(2, '0')} UTC`;

  return (
    <>
      <Tabs
        value={form.triggerType}
        onChange={(_, v) => setField('triggerType', v)}
        sx={{ minHeight: 36, mb: 2 }}
      >
        <Tab label="Event" value="event" sx={{ minHeight: 36, py: 0, textTransform: 'none', fontSize: 13 }} />
        <Tab label="Schedule" value="schedule" sx={{ minHeight: 36, py: 0, textTransform: 'none', fontSize: 13 }} />
      </Tabs>

      {form.triggerType === 'event' ? (
        <EventPatternEditor
          value={form.eventPattern}
          onChange={(v) => setField('eventPattern', v)}
          onValidityChange={onPatternValidityChange}
          error={hasAttemptedSave ? validationErrors.event_pattern : undefined}
        />
      ) : (
        <Box>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Button
              variant={!form.useCron ? 'contained' : 'outlined'}
              onClick={() => setField('useCron', false)}
              size="small"
            >
              Preset
            </Button>
            <Button
              variant={form.useCron ? 'contained' : 'outlined'}
              onClick={() => setField('useCron', true)}
              size="small"
            >
              Custom Cron
            </Button>
          </Box>

          {!form.useCron ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1 }}>
                {PRESETS.map((preset) => (
                  <Button
                    key={preset}
                    variant={form.schedulePreset === preset ? 'contained' : 'outlined'}
                    onClick={() => setField('schedulePreset', preset)}
                    size="small"
                    sx={{ textTransform: 'capitalize' }}
                  >
                    {preset}
                  </Button>
                ))}
              </Box>

              {showTimePicker && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <TimePicker
                    label="Time (your local time)"
                    value={timeValueLocal}
                    onChange={handleTimeChange}
                    ampm={false}
                    views={['hours', 'minutes']}
                    slotProps={{ textField: { size: 'small', sx: { width: 200 } } }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    = {utcLabel}
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            <TextField
              fullWidth
              size="small"
              label="Cron Expression"
              value={form.cronExpression}
              onChange={(e) => setField('cronExpression', e.target.value)}
              placeholder="0 9 ? * MON-FRI *"
              error={!!form.cronExpression && !/^(cron|rate)\(.+\)$/.test(form.cronExpression) && form.cronExpression.split(/\s+/).length !== 6}
              helperText={
                !form.cronExpression
                  ? 'Format: minute hour day-of-month month day-of-week year'
                  : /^(cron|rate)\(.+\)$/.test(form.cronExpression)
                    ? 'Full expression detected'
                    : form.cronExpression.split(/\s+/).length === 6
                      ? `Will be sent as: cron(${form.cronExpression})`
                      : `Expected 6 fields, got ${form.cronExpression.split(/\s+/).length}`
              }
              InputLabelProps={{ shrink: true }}
              InputProps={{ sx: { fontFamily: 'monospace' } }}
            />
          )}
        </Box>
      )}
    </>
  );
};

export default RuleScheduleSection;
